import type {
  FeedbackSupplierProfile,
  MassRenewalDownloadFile,
  MassRenewalLineSkipReason,
  MassRenewalRunReport,
  MassRenewalSkipReason,
} from '@erp/rpc';
import type { TranslateFn, UploadFileRejection } from 'src/lib/pricelist-utils';

import type { DroppedRenewalFile } from './classifyRenewalFiles';
import type { RenewalStartBlocker } from './massRenewalRunDisplay';

/**
 * The Mass Renewal card's wording for the run's client-neutral reasons and
 * the page's own states, so the view stays a layout. Every string is built
 * inside a function given `gt`, so the extractor sees it.
 */

/** What a dropped file was recognized as; null while it is still being read. */
export function fileKindText(
  kind: DroppedRenewalFile['kind'],
  gt: TranslateFn,
): string | null {
  switch (kind.kind) {
    case 'reading':
      return null;
    case 'results':
      return gt('Results Workbook', {
        context: 'Mass Renewal card: badge on the dropped Results Workbook',
      });
    case 'feedback':
      return gt('Feedback · {code} {division}', {
        context:
          'Mass Renewal card: badge on a dropped Feedback File; {code} is the Division code (WT), {division} its name',
        code: kind.division.code,
        division: kind.division.name,
      });
    case 'exceptions':
      if (kind.holdDivision)
        return gt('Product Holds · {code} {division}', {
          context:
            'Mass Renewal card: uploaded product list holds margin and core cost increases for every account and load in this Division, for this run only',
          code: kind.holdDivision.code,
          division: kind.holdDivision.name,
        });
      return gt('Exception List', {
        context: 'Mass Renewal card: badge on the dropped Exception List',
      });
    case 'unknown':
      return gt('Not recognized — not uploaded', {
        context:
          'Mass Renewal card: a dropped file is not a Results Workbook, Feedback File or Exception List',
      });
    case 'unreadable':
      return gt('Could not be opened as .xlsx — not uploaded', {
        context:
          'Mass Renewal card: the browser could not read a dropped file as an Excel workbook',
      });
  }
}

/** Why Start is disabled; null when the reason is plain from the card itself. */
export function renewalStartBlockerText(
  blocker: RenewalStartBlocker,
  gt: TranslateFn,
): string | null {
  if (typeof blocker !== 'string') return dropIssueText(blocker, gt);
  switch (blocker) {
    case 'active run':
      return gt('A run is in progress; Start is available once it finishes.', {
        context: 'Mass Renewal card: why the Start button is disabled',
      });
    case 'not allowed':
      return gt('Starting a run needs the Pricing create permission.', {
        context: 'Mass Renewal card: why the Start button is disabled',
      });
    case 'reading':
      return gt('Reading the dropped files…', {
        context:
          'Mass Renewal card: files are still being recognized before Start',
      });
    case 'starting':
      return null;
  }
}

function dropIssueText(
  issue: Exclude<RenewalStartBlocker, string>,
  gt: TranslateFn,
): string {
  switch (issue.issue) {
    case 'no results workbook':
      return gt('Drop the Results Workbook.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'several results workbooks':
      return gt('{count} Results Workbooks were dropped; remove all but one.', {
        context: 'Mass Renewal card: why Start is disabled',
        count: String(issue.count),
      });
    case 'no feedback file':
      return gt('Drop at least one filled-in Feedback File.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'too many feedback files':
      return gt('A run takes at most nine Feedback Files.', {
        context: 'Mass Renewal card: why Start is disabled',
      });
    case 'division has two feedback files':
      return gt('Two Feedback Files are for {code}; remove one.', {
        context:
          'Mass Renewal card: why Start is disabled; {code} is a Division code (WT)',
        code: issue.code,
      });
    case 'several exception lists':
      return gt('{count} Exception Lists were dropped; the limit is 20.', {
        context: 'Mass Renewal card: why Start is disabled',
        count: String(issue.count),
      });
  }
}

/** Why the run did not build a Division. */
export function divisionSkipReasonText(
  reason: MassRenewalSkipReason,
  gt: TranslateFn,
  /** The supplier's Results Workbook as the team names it ("FINAL.xlsx"). */
  results: string,
  /** For a Feedback File that does not match: the share of its rows that agreed (0–1). */
  share?: number,
): string {
  switch (reason) {
    case 'no feedback file':
      return gt('No Feedback File', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'two feedback files name this Division':
      return gt('Two Feedback Files name this Division', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'tab not in the Results Workbook':
      return gt('Not in {results}', {
        context:
          'Mass Renewal card: why a Division was not built; {results} names the Results Workbook',
        results,
      });
    case 'tab not in the Division table':
      return gt('Not in the Division table', {
        context: 'Mass Renewal card: why a Division was not built',
      });
    case 'feedback file does not match the results':
      return gt(
        'Feedback File does not match {results}: only {percent}% of its Loads have the same Quote SPA',
        {
          context:
            'Mass Renewal card: why a Division was not built; the Feedback File looks made for another supplier’s results. {results} names the Results Workbook, {percent} is a whole number',
          results,
          percent: String(Math.round((share ?? 0) * 100)),
        },
      );
  }
}

/** Why lines were left out of both uploads before pricing. */
export function lineSkipReasonText(
  reason: MassRenewalLineSkipReason,
  gt: TranslateFn,
): string {
  switch (reason) {
    case 'no AS400 Item':
      return gt('no AS400 Item', {
        context: 'Mass Renewal card: why lines were left out of the upload',
      });
    case 'blank Pricing Factor':
      return gt('blank Pricing Factor', {
        context: 'Mass Renewal card: why lines were left out of the upload',
      });
    case 'blank FINAL PRIOR MARGINS':
      return gt('blank FINAL PRIOR MARGINS', {
        context:
          'Mass Renewal card: why lines were left out of the upload; FINAL PRIOR MARGINS is a column name',
      });
    case 'blank quote cost':
      return gt('blank quote cost', {
        context:
          'Mass Renewal card: why lines were left out of the upload; the QUOTE COST PER EACH column is blank',
      });
    case 'zero quote cost':
      return gt('zero quote cost', {
        context:
          'Mass Renewal card: why lines were left out of the upload; the QUOTE COST PER EACH column is 0',
      });
  }
}

type UnmatchedExceptionRow =
  MassRenewalRunReport['unmatchedExceptionRows'][number];

/** Why an Exception List row priced no line. */
export function unmatchedExceptionReasonText(
  row: Pick<UnmatchedExceptionRow, 'reason' | 'failure'>,
  gt: TranslateFn,
): string {
  switch (row.reason) {
    case 'no line with this item':
      return gt('no line has this item', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; no line of the built Divisions has its AS400 item',
      });
    case 'no line matched its filters':
      return gt('no line matched its Account, Group, Load, SPA or Division', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; lines have its item but not its other filters',
      });
    case 'matched only lines that failed':
      return gt('matched only lines that failed: {failure}', {
        context:
          'Mass Renewal card: why an Exception List row priced no line; {failure} is the first failed line’s problem, from the server',
        failure: row.failure ?? '',
      });
    case 'matched only lines of a skipped Division':
      return gt(
        'matched only lines of a Division that was skipped (its Feedback File does not match the results)',
        {
          context:
            'Mass Renewal card: why an Exception List row priced no line; the only lines it matched belong to a Division the run skipped',
        },
      );
  }
}

/** "3 no AS400 Item · 1 blank Pricing Factor": the skipped lines by reason. */
export function skippedBreakdownText(
  skipped: Readonly<Record<MassRenewalLineSkipReason, number>>,
  gt: TranslateFn,
): string {
  return (Object.entries(skipped) as [MassRenewalLineSkipReason, number][])
    .flatMap(([reason, count]) =>
      count > 0 ? [`${count} ${lineSkipReasonText(reason, gt)}`] : [],
    )
    .join(' · ');
}

/** A Division's download button label. */
export function downloadFileText(
  file: MassRenewalDownloadFile,
  gt: TranslateFn,
): string {
  switch (file) {
    case 'upload':
      return gt('Upload CSV', {
        context:
          "Mass Renewal card: button that downloads a Division's AS400 upload CSV",
      });
    case 'exceptions':
      return gt('Exceptions CSV', {
        context:
          "Mass Renewal card: button that downloads a Division's Exception List lines CSV",
      });
    case 'report':
      return gt('Report', {
        context:
          "Mass Renewal card: button that downloads a Division's Renewal Report workbook",
      });
  }
}

/** The run reads `.xlsx` only; said at the drop, not after an upload. */
export function validateRenewalFile(
  file: File,
  gt: TranslateFn,
  supplier: FeedbackSupplierProfile,
): UploadFileRejection | null {
  if (/\.xlsx$/i.test(file.name)) return null;
  return {
    kind: 'unsupported',
    title: gt('Unsupported file format', {
      context:
        "toast title when a non-.xlsx file is dropped on a supplier's Mass Renewal card",
    }),
    message: gt(
      '{supplier} Mass Renewal reads .xlsx workbooks only: {results}, the Feedback Files and the Exception List.',
      {
        context:
          "toast message when a non-.xlsx file is dropped on a supplier's Mass Renewal card; {supplier} is the supplier (Hubbell, ABB), {results} names its Results Workbook",
        supplier: supplier.name,
        results: supplier.resultsFileLabel,
      },
    ),
  };
}
